import { Grid, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import AryCurrencyTextField from 'framework/base/components/AryCurrencyTextField';
import AryCustomerOrBankSelect from 'framework/base/components/AryCustomerOrBankSelect';
import AryDatePicker from 'framework/base/components/AryDatePicker';
import AryDateTimePicker from 'framework/base/components/AryDateTimePicker';
import AryLoadingButton from 'framework/base/components/AryLoadingButton';
import ArySelectTransactionGroups from 'framework/base/components/ArySelectTransactionGroups';
import AryWrapper from 'framework/base/components/AryWrapper';
import {
  GET_ACCOUNTS,
  GET_BANK_MAIN_CURRENCY,
  GET_STATE
} from 'framework/base/constants/aryGraphqlSchemas';
import {
  SUBMIT_TRADE_CURRENCY,
  UPDATE_TRADE_CURRENCY
} from 'framework/base/constants/aryRestCommands';
import aryRestMethods from 'framework/base/constants/aryRestMethods';
import arySystemSettings from 'framework/base/constants/arySystemSettings';
import useAryDelayedRestData from 'framework/base/hooks/useAryDelayedRestData';
import { useAryLazyQuery } from 'framework/base/hooks/useAryGraphql';
import useSystemSettingsContext from 'framework/base/hooks/useSystemSettingsContext';
import { getMoonUtcTime, getUtcTime } from 'framework/base/utils/Utils';
import AryLoadingAnimation from 'framework/desktop/AryLoadingAnimation';
import AryFileUploaderModal from 'framework/desktop/AryNewFileUploaderModal';
import AccountStatus from 'framework/desktop/opponent-type-components/AccountStatus';
import BankStatus from 'framework/desktop/opponent-type-components/BankStatus';
import CommissionComponentChip from 'framework/desktop/switchable-chips/CommissionComponentChip';
import PrivateIcon from 'framework/desktop/switchable-chips/SVGs/PrivateIcon';
import SyncCreditLimitIcon from 'framework/desktop/switchable-chips/SVGs/SyncCreditLimitIcon';
import SwitchableChip from 'framework/desktop/switchable-chips/SwitchableChip';
import WhatsappChips from 'framework/desktop/switchable-chips/WhatsappChips';
import AryTriplexComponent from 'framework/desktop/triplex-component/AryTriplexComponent';
import { areTriplexValuesUnchanged } from 'framework/desktop/triplex-component/utils';
import { tradeCurrencyValidationSchema } from 'pages/validations/aryFormValidations';
import React, { Suspense, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuth } from 'spec';

const BuyCurrencyReceiptModal = React.lazy(() =>
  import('./BuyCurrencyReceiptModal')
);
const BuyCurrencyBase = ({
  handleChangeCustomerAccountNumber,
  handleChangeBankAccountId,
  closeParentModal,
  tradeCurrencyData,
  transactionId,
  accountNumber,
  bankAccountId,
  submitButtonSize,
  setParentLoading = () => { }
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const auth = useAuth();
  const buyerBankAccountRef = useRef();
  const buyerAccountRef = useRef();
  const sellerBankAccountRef = useRef();
  const sellerAccountRef = useRef();
  const whatsappLoginState = useSelector(
    (state) => state.systemReducer.whatsapp.isLogin
  );

  const hasWhatsappAccess = auth.WhatsappA;
  const { PrivateTransactionR } = auth;

  const [modalOpen, setModalOpen] = useState(false);
  const [hasBuyCommission, setHasBuyCommission] = useState(false);
  const [hasSellCommission, setHasSellCommission] = useState(false);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [submittedTransactionId, setSubmittedTransactionId] = useState('');
  const [isTradeRateValid, setIsTradeRateValid] = useState('');
  const [isCalculationDone, setIsCalculationDone] = useState(true);

  const { systemSettings } = useSystemSettingsContext();

  // Initial Triplex values (mapped for both trade currency and generic triplex keys)
  const initialTriplexValues = {
    buyAmount: tradeCurrencyData?.buyAmount ?? '',
    buyCurrencyCode: tradeCurrencyData?.buyCurrencyCode || '',
    sellAmount: tradeCurrencyData?.sellAmount ?? '',
    sellCurrencyCode: tradeCurrencyData?.sellCurrencyCode || '',
    tradeRate: tradeCurrencyData?.tradeRate || '',
    draftAmount: tradeCurrencyData?.buyAmount ?? '',
    draftCurrencyCode: tradeCurrencyData?.buyCurrencyCode || '',
    receivedAmount: tradeCurrencyData?.sellAmount ?? '',
    receivedCurrencyCode: tradeCurrencyData?.sellCurrencyCode || ''
  };

  const [submitTradeCurrency, { loading: submitTradeCurrencyLoading }] =
    useAryDelayedRestData({
      api: SUBMIT_TRADE_CURRENCY,
      type: GET_STATE.type.TRADE_CURRENCY,
      onSucceed: ({ transactionId: buyCurrencyTransactionId }) => {
        setSubmittedTransactionId(buyCurrencyTransactionId);
        setModalOpen(true);
      }
    });

  const [updateTradeCurrency, { loading: updateTradeCurrencyLoading }] =
    useAryDelayedRestData({
      api: UPDATE_TRADE_CURRENCY,
      type: GET_STATE.type.TRADE_CURRENCY,
      method: aryRestMethods.put,
      onSucceed: () => {
        setModalOpen(true);
      }
    });

  const [getBankMainCurrency] = useAryLazyQuery({
    gqlConfig: GET_BANK_MAIN_CURRENCY,
    items: 'currencyCode'
  });

  const formik = useFormik({
    initialValues: {
      transactionId,
      isBuy: true,
      date: new Date(),
      isPrivate: false,
      buyAmount: tradeCurrencyData?.buyAmount || '',
      buyCurrencyCode: tradeCurrencyData?.buyCurrencyCode || '',
      buyerAccountNumber: accountNumber || bankAccountId || '',
      tradeRate: tradeCurrencyData?.tradeRate || '',
      buyCommissionAmount: '',
      buyCommissionCurrencyCode: '',
      buyCommissionIsDebtor: true,
      profitAmount: '',
      profitCurrency: '',
      buyerIsBank: !!bankAccountId,
      buyerIsNewBankTransaction: true,
      buyerReceiptNumber: '',
      buyerTransactionId: '',
      buyerBankReceiptDate: new Date(),
      sellAmount: tradeCurrencyData?.sellAmount || '',
      sellCurrencyCode: tradeCurrencyData?.sellCurrencyCode || '',
      sellerAccountNumber: accountNumber || '',
      sellCommissionAmount: '',
      sellCommissionCurrencyCode: '',
      sellCommissionIsDebtor: true,
      sellerIsBank: false,
      sellerIsNewBankTransaction: true,
      sellerReceiptNumber: '',
      sellerTransactionId: '',
      sellerBankReceiptDate: new Date(),
      description: '',
      shouldSyncCreditLimit:
        systemSettings[arySystemSettings.defaultUpdateCreditLimit],
      isPublishToWhatsApp:
        systemSettings[arySystemSettings.defaultSendWhatsApp] &&
        whatsappLoginState,
      files: [],
      fileIds: [],
      sourceOfMoney: '',
      purposeOfExchange: '',
      transactionCategory: []
    },
    validationSchema: tradeCurrencyValidationSchema(t),
    onSubmit: (values) => {
      const currentValuesForCheck = {
        buyAmount: values.buyAmount,
        buyCurrencyCode: values.buyCurrencyCode,
        sellAmount: values.sellAmount,
        sellCurrencyCode: values.sellCurrencyCode,
        tradeRate: values.tradeRate,
        draftAmount: values.buyAmount,
        draftCurrencyCode: values.buyCurrencyCode,
        receivedAmount: values.sellAmount,
        receivedCurrencyCode: values.sellCurrencyCode
      };

      const isTriplexValuesUnchangedOnSubmit =
        Boolean(transactionId) &&
        Boolean(tradeCurrencyData) &&
        areTriplexValuesUnchanged(currentValuesForCheck, initialTriplexValues);

      // Ignore tradeRate validity when in edit mode and Triplex values are unchanged
      if (!isTradeRateValid && !isTriplexValuesUnchangedOnSubmit) return;

      let tradeCurrencyItem;
      if (transactionId) {
        const {
          files,
          buyerIsBank,
          sellerIsBank,
          buyerAccountNumber,
          sellerAccountNumber,
          buyerIsNewBankTransaction,
          sellerIsNewBankTransaction,
          buyerReceiptNumber,
          buyerTransactionId,
          sellerReceiptNumber,
          sellerTransactionId,
          buyerBankReceiptDate,
          sellerBankReceiptDate,
          ...others
        } = {
          ...values
        };
        others.newFiles = files;
        others.removedFileIds = removedFileIds;
        tradeCurrencyItem = others;
        console.warn(
          'in case that we have no time to fix isCalculationDone so i try to mastmali fani',
          isCalculationDone
        );
      } else {
        const {
          transactionId: id,
          buyerIsNewBankTransaction,
          sellerIsNewBankTransaction,
          ...others
        } = { ...values };
        tradeCurrencyItem = others;
        if (values.buyerIsBank && values.buyerIsNewBankTransaction) {
          tradeCurrencyItem.buyerBankReceiptDate = getUtcTime(
            tradeCurrencyItem.buyerBankReceiptDate
          );
        } else {
          tradeCurrencyItem.buyerBankReceiptDate = null;
        }
        if (values.sellerIsBank && values.sellerIsNewBankTransaction) {
          tradeCurrencyItem.sellerBankReceiptDate = getUtcTime(
            tradeCurrencyItem.sellerBankReceiptDate
          );
        } else {
          tradeCurrencyItem.sellerBankReceiptDate = null;
        }
      }

      tradeCurrencyItem.date = getMoonUtcTime(tradeCurrencyItem.date);

      if (hasBuyCommission) {
        tradeCurrencyItem.buyCommissionAmount =
          tradeCurrencyItem.buyCommissionAmount || undefined;
      } else {
        tradeCurrencyItem.buyCommissionCurrencyCode = '';
        tradeCurrencyItem.buyCommissionAmount = undefined;
      }
      if (hasSellCommission) {
        tradeCurrencyItem.sellCommissionAmount =
          tradeCurrencyItem.sellCommissionAmount || undefined;
      } else {
        tradeCurrencyItem.sellCommissionCurrencyCode = '';
        tradeCurrencyItem.sellCommissionAmount = undefined;
      }

      if (transactionId) {
        updateTradeCurrency({
          ...tradeCurrencyItem
        });
      } else {
        submitTradeCurrency({
          ...tradeCurrencyItem
        });
      }
    }
  });

  // Check if Triplex values are untouched in update mode
  const currentTriplexValues = {
    buyAmount: formik.values.buyAmount,
    buyCurrencyCode: formik.values.buyCurrencyCode,
    sellAmount: formik.values.sellAmount,
    sellCurrencyCode: formik.values.sellCurrencyCode,
    tradeRate: formik.values.tradeRate,
    draftAmount: formik.values.buyAmount,
    draftCurrencyCode: formik.values.buyCurrencyCode,
    receivedAmount: formik.values.sellAmount,
    receivedCurrencyCode: formik.values.sellCurrencyCode
  };

  const isTriplexUnchanged =
    Boolean(transactionId) &&
    Boolean(tradeCurrencyData) &&
    areTriplexValuesUnchanged(currentTriplexValues, initialTriplexValues);

  useEffect(() => {
    if (!hasBuyCommission) {
      formik.setFieldValue('buyCommissionAmount', '');
    }
  }, [hasBuyCommission]);

  useEffect(() => {
    if (!hasSellCommission) {
      formik.setFieldValue('sellCommissionAmount', '');
    }
  }, [hasSellCommission]);

  useEffect(() => {
    if (tradeCurrencyData) {
      formik.setValues({
        ...formik.values,
        transactionId,
        date: new Date(+tradeCurrencyData.date),
        isPrivate: tradeCurrencyData.isPrivate,
        buyAmount: tradeCurrencyData.buyAmount,
        tradeRate: tradeCurrencyData.tradeRate,
        buyCurrencyCode: tradeCurrencyData.buyCurrencyCode,
        buyerAccountNumber: tradeCurrencyData.buyerAccountNumber,
        buyerIsBank: tradeCurrencyData.buyerIsBank,
        buyCommissionAmount: tradeCurrencyData.buyCommissionAmount || undefined,
        buyCommissionCurrencyCode: tradeCurrencyData.buyCommissionCurrencyCode,
        buyCommissionIsDebtor: tradeCurrencyData.buyCommissionIsDebtor,
        sellAmount: tradeCurrencyData.sellAmount,
        sellCurrencyCode: tradeCurrencyData.sellCurrencyCode,
        sellerAccountNumber: tradeCurrencyData.sellerAccountNumber,
        sellerIsBank: tradeCurrencyData.sellerIsBank,
        sellCommissionAmount:
          tradeCurrencyData.sellCommissionAmount || undefined,
        sellCommissionCurrencyCode:
          tradeCurrencyData.sellCommissionCurrencyCode,
        sellCommissionIsDebtor: tradeCurrencyData.sellCommissionIsDebtor,
        description: tradeCurrencyData.description,
        files: [],
        fileIds: tradeCurrencyData.fileIds,
        sourceOfMoney: tradeCurrencyData.sourceOfMoney,
        purposeOfExchange: tradeCurrencyData.purposeOfExchange,
        transactionCategory: tradeCurrencyData.transactionCategory
      });
      setHasBuyCommission(!!tradeCurrencyData.buyCommissionAmount);
      setHasSellCommission(!!tradeCurrencyData.sellCommissionAmount);
    }
  }, [tradeCurrencyData]);

  useEffect(() => {
    (async () => {
      if (bankAccountId && !transactionId) {
        const resp = await getBankMainCurrency(bankAccountId);
        formik.setFieldValue('buyCurrencyCode', resp?.data?.currencyCode);
        formik.setFieldValue('buyCommissionCurrencyCode', resp?.data?.currencyCode);
      }
    })();
  }, [bankAccountId]);

  useEffect(() => {
    if (formik.values.sellCommissionCurrencyCode) {
      setHasSellCommission(true);
    }

    if (formik.values.buyCommissionCurrencyCode) {
      setHasBuyCommission(true);
    }
  }, [
    formik.values.buyCommissionCurrencyCode,
    formik.values.sellCommissionCurrencyCode
  ]);

  useEffect(() => {
    setParentLoading(submitTradeCurrencyLoading || updateTradeCurrencyLoading);
  }, [submitTradeCurrencyLoading, updateTradeCurrencyLoading]);

  const handleResetForm = () => {
    formik.setValues({
      ...formik.values,
      transactionId: '',
      isPrivate: false,
      buyAmount: '',
      tradeRate: '',
      buyCurrencyCode: '',
      buyerAccountNumber: formik.values.buyerAccountNumber,
      buyerIsBank: formik.values.buyerIsBank,
      buyCommissionAmount: '',
      buyCommissionCurrencyCode: '',
      buyCommissionIsDebtor: false,
      profitAmount: '',
      profitCurrency: '',
      sellAmount: '',
      sellCurrencyCode: '',
      sellerAccountNumber: formik.values.sellerAccountNumber,
      sellerIsBank: formik.values.sellerIsBank,
      sellCommissionAmount: '',
      sellCommissionCurrencyCode: '',
      sellCommissionIsDebtor: false,
      description: '',
      files: [],
      fileIds: [],
      sourceOfMoney: '',
      purposeOfExchange: '',
      transactionCategory: []
    });
    formik.setErrors({});
    formik.setTouched({});
  };

  return (
    <>
      <form
        onSubmit={formik.handleSubmit}
        autoComplete="off"
        style={{ height: '100%', display: 'inline-block' }}
        id="buy-currency"
      >
        <Grid
          container
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            rowSpacing={theme.padding.gridRowSpacing}
            columnSpacing={theme.padding.gridColumnSpacing}
            maxHeight="calc(100vh - 10.5rem)"
            overflow="auto"
          >
            <Grid item xs={12}>
              <AryWrapper>
                <Grid
                  container
                  rowSpacing={theme.padding.gridSmallRowSpacing}
                  columnSpacing={theme.padding.gridSmallColumnSpacing}
                >
                  <Grid item xs={6}>
                    <AryDatePicker
                      label={t('Date')}
                      fullWidth
                      disabled={
                        formik.values.buyerIsBank && formik.values.sellerIsBank
                      }
                      value={
                        formik.values.buyerIsBank && formik.values.sellerIsBank
                          ? null
                          : formik.values.date
                      }
                      onChange={(newValue) => {
                        formik.setFieldValue('date', newValue);
                      }}
                      error={formik.touched.date && !!formik.errors.date}
                      helperText={formik.touched.date && formik.errors.date}
                      onBlur={() => {
                        formik.setFieldTouched('date', true);
                      }}
                      iconSize="1.125rem"
                      tabIndex={-1}
                    />
                  </Grid>
                  <Grid item xs={6} />
                  <Grid item xs={6}>
                    <AryCustomerOrBankSelect
                      label={`${t('Debtor Customer')} (${t('Seller')})`}
                      focused
                      value={formik.values.buyerAccountNumber}
                      accountType={GET_ACCOUNTS.accountType.ALL_CAN_TRANSACT}
                      handleChangeAccountId={async (selectedObj) => {
                        if (!formik.values.buyerIsBank || !selectedObj?.value) {
                          formik.setValues({
                            ...formik.values,
                            buyerAccountNumber: selectedObj?.value || '',
                            buyerTransactionId: ''
                          });
                        } else {
                          if (selectedObj?.value === -1) return;
                          const resp = await getBankMainCurrency(
                            selectedObj?.value
                          );
                          const newCurrencyCode = resp.data.currencyCode;
                          if (formik.values.buyCurrencyCode === newCurrencyCode) {
                            formik.setValues({
                              ...formik.values,
                              buyerAccountNumber: selectedObj?.value || '',
                              buyerTransactionId: '',
                              buyCommissionCurrencyCode: newCurrencyCode
                            });
                          } else {
                            formik.setValues({
                              ...formik.values,
                              buyerAccountNumber: selectedObj?.value || '',
                              buyCurrencyCode: newCurrencyCode,
                              buyAmount:
                                newCurrencyCode === formik.values.sellCurrencyCode
                                  ? formik.values.sellAmount
                                  : formik.values.buyAmount,
                              buyerTransactionId: '',
                              buyCommissionCurrencyCode: newCurrencyCode,
                              buyerReceiptNumber: ''
                            });
                          }
                        }
                      }}
                      disabled={!!transactionId}
                      isBank={formik.values.buyerIsBank}
                      excludedBankIds={
                        formik.values.sellerIsBank &&
                        formik.values.sellerAccountNumber &&
                        [].concat(formik.values.sellerAccountNumber)
                      }
                      excludedMainCurrencies={
                        formik.values.sellCurrencyCode
                          ? [].concat(formik.values.sellCurrencyCode)
                          : []
                      }
                      handleChangeIsBank={(newValue) => {
                        formik.setValues({
                          ...formik.values,
                          buyerAccountNumber: '',
                          buyerIsBank: newValue
                        });
                      }}
                      error={
                        formik.touched.buyerAccountNumber &&
                        !!formik.errors.buyerAccountNumber
                      }
                      helperText={
                        formik.touched.buyerAccountNumber &&
                        formik.errors.buyerAccountNumber
                      }
                      onBlur={() => {
                        formik.setFieldTouched('buyerAccountNumber', true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {!formik.values.buyerIsBank && (
                      <AccountStatus
                        currencyCode={formik.values.buyCurrencyCode}
                        accountNumber={formik.values.buyerAccountNumber}
                        ref={buyerAccountRef}
                        isCalculationDoneHandler={(newValue) => {
                          setIsCalculationDone(newValue);
                        }}
                        message={t('AccountStatusMessage')}
                      />
                    )}
                    {formik.values.buyerIsBank && (
                      <BankStatus
                        isDeposit
                        ref={buyerBankAccountRef}
                        bankId={formik.values.buyerAccountNumber}
                        isNewTransaction={formik.values.buyerIsNewBankTransaction}
                        handleIsNewTransactionChange={() => {
                          formik.setValues({
                            ...formik.values,
                            buyerIsNewBankTransaction:
                              !formik.values.buyerIsNewBankTransaction,
                            buyerBankReceiptDate: null,
                            buyerReceiptNumber: ''
                          });
                        }}
                        receiptNumber={formik.values.receiptNumber}
                        handleNewTransactionSelected={(receiptNumber) => {
                          formik.setValues({
                            ...formik.values,
                            documentId: '',
                            receiptNumber
                          });
                        }}
                        handleOldTransactionSelected={(
                          documentId,
                          buyerReceiptNumber,
                          buyerBankReceiptDate,
                          buyAmount
                        ) => {
                          const newFormikValues = { ...formik.values };
                          formik.setValues({
                            ...newFormikValues,
                            buyerTransactionId: documentId,
                            buyerReceiptNumber,
                            buyerBankReceiptDate: new Date(+buyerBankReceiptDate),
                            buyAmount
                          });
                        }}
                        isCalculationDoneHandler={(newValue) => {
                          setIsCalculationDone(newValue);
                        }}
                        receiptNumberError={
                          formik.touched.buyerReceiptNumber &&
                          !!formik.errors.buyerReceiptNumber
                        }
                        receiptNumberHelperText={
                          formik.touched.buyerReceiptNumber &&
                          formik.errors.buyerReceiptNumber
                        }
                        documentIdError={
                          formik.touched.documentId && !!formik.errors.documentId
                        }
                        documentIdHelperText={
                          formik.touched.documentId && formik.errors.documentId
                        }
                        receiptNumberOnBlur={() =>
                          formik.setFieldTouched('buyerReceiptNumber', true)
                        }
                        bankReceiptDate={formik.values.buyerBankReceiptDate}
                        handleChangeBankReceiptDate={(newDate) =>
                          formik.setFieldValue('buyerBankReceiptDate', newDate)
                        }
                        bankReceiptDateTouched={
                          formik.touched.buyerBankReceiptDate
                        }
                        bankReceiptDateError={formik.errors.buyerBankReceiptDate}
                        setBankReceiptDateTouched={() =>
                          formik.setFieldTouched('buyerBankReceiptDate', true)
                        }
                        message={t('bankStatusMessage')}
                      />
                    )}
                  </Grid>
                  {formik.values.buyerIsBank && (
                    <Grid item xs={12}>
                      <Grid
                        container
                        rowSpacing={theme.padding.gridSmallRowSpacing}
                        columnSpacing={theme.padding.gridSmallColumnSpacing}
                      >
                        <Grid item xs={6}>
                          <AryDateTimePicker
                            label={t('Transaction Date')}
                            size="medium"
                            disabled={
                              !formik.values.buyerIsNewBankTransaction
                            }
                            value={formik.values.buyerBankReceiptDate}
                            onChange={(newDate) => {
                              formik.setFieldValue(
                                'buyerBankReceiptDate',
                                newDate
                              );
                              formik.setFieldValue(
                                'sellerBankReceiptDate',
                                newDate
                              );
                            }}
                            fullWidth
                            onBlur={() =>
                              formik.setFieldTouched('buyerBankReceiptDate', true)
                            }
                            error={
                              formik.touched.buyerBankReceiptDate &&
                              !!formik.errors.buyerBankReceiptDate
                            }
                            helperText={
                              formik.touched.buyerBankReceiptDate &&
                              formik.errors.buyerBankReceiptDate
                            }
                            iconSize="1.125rem"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label={t('Transaction Number')}
                            disabled={!formik.values.buyerIsNewBankTransaction}
                            value={formik.values.buyerReceiptNumber}
                            onChange={(event) => {
                              const { value } = event.target;
                              if (value.trimStart().length <= 9) {
                                formik.setValues({
                                  ...formik.values,
                                  documentId: '',
                                  buyerReceiptNumber: value.trimStart()
                                });
                              }
                            }}
                            error={
                              formik.touched.buyerReceiptNumber &&
                              !!formik.errors.buyerReceiptNumber
                            }
                            helperText={
                              formik.touched.buyerReceiptNumber &&
                              formik.errors.buyerReceiptNumber
                            }
                            onBlur={() =>
                              formik.setFieldTouched('buyerReceiptNumber', true)
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <AryCustomerOrBankSelect
                      label={`${t('Creditor Customer')} (${t('Buyer')})`}
                      value={formik.values.sellerAccountNumber}
                      handleChangeAccountId={async (selectedObj) => {
                        if (!formik.values.sellerIsBank || !selectedObj?.value) {
                          formik.setValues({
                            ...formik.values,
                            sellerAccountNumber: selectedObj?.value || '',
                            sellerTransactionId: ''
                          });
                        } else {
                          if (selectedObj?.value === -1) return;
                          const resp = await getBankMainCurrency(
                            selectedObj?.value
                          );
                          const newCurrencyCode = resp.data.currencyCode;
                          if (
                            formik.values.sellCurrencyCode === newCurrencyCode
                          ) {
                            formik.setValues({
                              ...formik.values,
                              sellerAccountNumber: selectedObj?.value || '',
                              sellerTransactionId: '',
                              sellCommissionCurrencyCode: newCurrencyCode
                            });
                          } else {
                            formik.setValues({
                              ...formik.values,
                              sellerAccountNumber: selectedObj?.value || '',
                              sellCurrencyCode: newCurrencyCode,
                              sellAmount:
                                newCurrencyCode === formik.values.buyCurrencyCode
                                  ? formik.values.buyAmount
                                  : formik.values.sellAmount,
                              sellerTransactionId: '',
                              sellCommissionCurrencyCode: newCurrencyCode,
                              sellerBankReceiptDate: null,
                              sellerReceiptNumber: ''
                            });
                          }
                        }
                      }}
                      disabled={!!transactionId}
                      isBank={formik.values.sellerIsBank}
                      excludedBankIds={
                        formik.values.buyerIsBank &&
                        formik.values.buyerAccountNumber &&
                        [].concat(formik.values.buyerAccountNumber)
                      }
                      excludedMainCurrencies={
                        formik.values.buyCurrencyCode
                          ? [].concat(formik.values.buyCurrencyCode)
                          : []
                      }
                      handleChangeIsBank={(newValue) => {
                        formik.setValues({
                          ...formik.values,
                          sellerAccountNumber: '',
                          sellerIsBank: newValue
                        });
                      }}
                      error={
                        formik.touched.sellerAccountNumber &&
                        !!formik.errors.sellerAccountNumber
                      }
                      helperText={
                        formik.touched.sellerAccountNumber &&
                        formik.errors.sellerAccountNumber
                      }
                      onBlur={() => {
                        formik.setFieldTouched('sellerAccountNumber', true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {!formik.values.sellerIsBank && (
                      <AccountStatus
                        currencyCode={formik.values.sellCurrencyCode}
                        accountNumber={formik.values.sellerAccountNumber}
                        ref={sellerAccountRef}
                        isCalculationDoneHandler={(newValue) => {
                          setIsCalculationDone(newValue);
                        }}
                        message={t('AccountStatusMessage')}
                      />
                    )}
                    {formik.values.sellerIsBank && (
                      <BankStatus
                        isDeposit={false}
                        ref={sellerBankAccountRef}
                        bankId={formik.values.sellerAccountNumber}
                        isNewTransaction={
                          formik.values.sellerIsNewBankTransaction
                        }
                        handleIsNewTransactionChange={() =>
                          formik.setValues({
                            ...formik.values,
                            sellerIsNewBankTransaction:
                              !formik.values.sellerIsNewBankTransaction,
                            sellerBankReceiptDate: null,
                            sellerReceiptNumber: ''
                          })
                        }
                        receiptNumber={formik.values.receiptNumber}
                        handleNewTransactionSelected={(receiptNumber) => {
                          formik.setValues({
                            ...formik.values,
                            documentId: '',
                            receiptNumber
                          });
                        }}
                        handleOldTransactionSelected={(
                          documentId,
                          sellerReceiptNumber,
                          sellerBankReceiptDate,
                          sellAmount
                        ) => {
                          const newFormikValues = { ...formik.values };

                          formik.setValues({
                            ...newFormikValues,
                            sellerTransactionId: documentId,
                            sellerReceiptNumber,
                            sellerBankReceiptDate: new Date(
                              +sellerBankReceiptDate
                            ),
                            sellAmount
                          });
                        }}
                        isCalculationDoneHandler={(newValue) => {
                          setIsCalculationDone(newValue);
                        }}
                        receiptNumberError={
                          formik.touched.sellerReceiptNumber &&
                          !!formik.errors.sellerReceiptNumber
                        }
                        receiptNumberHelperText={
                          formik.touched.sellerReceiptNumber &&
                          formik.errors.sellerReceiptNumber
                        }
                        documentIdError={
                          formik.touched.documentId && !!formik.errors.documentId
                        }
                        documentIdHelperText={
                          formik.touched.documentId && formik.errors.documentId
                        }
                        receiptNumberOnBlur={() =>
                          formik.setFieldTouched('sellerReceiptNumber', true)
                        }
                        bankReceiptDate={formik.values.sellerBankReceiptDate}
                        handleChangeBankReceiptDate={(newDate) =>
                          formik.setFieldValue('sellerBankReceiptDate', newDate)
                        }
                        bankReceiptDateTouched={
                          formik.touched.sellerBankReceiptDate
                        }
                        bankReceiptDateError={
                          formik.touched.sellerBankReceiptDate &&
                          !!formik.errors.sellerBankReceiptDate
                        }
                        setBankReceiptDateTouched={() =>
                          formik.setFieldTouched('sellerBankReceiptDate', true)
                        }
                        message={t('bankStatusMessage')}
                      />
                    )}
                  </Grid>
                  {formik.values.sellerIsBank && (
                    <Grid item xs={12}>
                      <Grid
                        container
                        rowSpacing={theme.padding.gridSmallRowSpacing}
                        columnSpacing={theme.padding.gridSmallColumnSpacing}
                      >
                        <Grid item xs={6}>
                          <AryDateTimePicker
                            label={t('Transaction Date')}
                            size="medium"
                            disabled={
                              !formik.values.sellerIsNewBankTransaction
                            }
                            value={formik.values.sellerBankReceiptDate}
                            onChange={(newDate) =>
                              formik.setFieldValue(
                                'sellerBankReceiptDate',
                                newDate
                              )
                            }
                            fullWidth
                            onBlur={() =>
                              formik.setFieldTouched(
                                'sellerBankReceiptDate',
                                true
                              )
                            }
                            error={
                              formik.touched.sellerBankReceiptDate &&
                              !!formik.errors.sellerBankReceiptDate
                            }
                            helperText={
                              formik.touched.sellerBankReceiptDate &&
                              formik.errors.sellerBankReceiptDate
                            }
                            iconSize="1.125rem"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label={t('Transaction Number')}
                            disabled={!formik.values.sellerIsNewBankTransaction}
                            value={formik.values.sellerReceiptNumber}
                            onChange={(event) => {
                              const { value } = event.target;
                              if (value.trimStart().length <= 9) {
                                formik.setValues({
                                  ...formik.values,
                                  documentId: '',
                                  sellerReceiptNumber: value.trimStart()
                                });
                              }
                            }}
                            error={
                              formik.touched.sellerReceiptNumber &&
                              !!formik.errors.sellerReceiptNumber
                            }
                            helperText={
                              formik.touched.sellerReceiptNumber &&
                              formik.errors.sellerReceiptNumber
                            }
                            onBlur={() =>
                              formik.setFieldTouched('sellerReceiptNumber', true)
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid
                    item
                    container
                    rowSpacing={theme.padding.gridSmallRowSpacing}
                    columnSpacing={theme.padding.gridSmallColumnSpacing}
                  >
                    <Grid item xs={12}>
                      <AryTriplexComponent
                        initialProps={{
                          currencyLabel: 'Buy Currency',
                          currencyCode: formik.values.buyCurrencyCode,
                          currencyCodeOnChange: (selectedObj) => {
                            formik.setFieldValue(
                              'buyCurrencyCode',
                              selectedObj.value
                            );
                          },
                          excludedCurrencyCodes: [formik.values.sellCurrencyCode],
                          currencyCodeError:
                            formik.touched.buyCurrencyCode &&
                            !!formik.errors.buyCurrencyCode,
                          currencyCodeHelperText:
                            formik.touched.buyCurrencyCode &&
                            formik.errors.buyCurrencyCode,
                          currencyCodeSx: {
                            dropdownIndicator: {
                              paddingTop: '0',
                              paddingBottom: '0'
                            }
                          },
                          amountValue: formik.values.buyAmount,
                          amountLabel: 'Buy Amount',
                          amountValueOnChange: (newValue) => {
                            formik.setFieldValue('buyAmount', newValue);
                          },
                          onBlur: () => {
                            formik.setFieldTouched('buyAmount', true);
                          },
                          amountError:
                            (formik.touched.buyAmount ||
                              formik.touched.buyCurrencyCode) &&
                            (!!formik.errors.buyAmount ||
                              !!formik.errors.buyCurrencyCode),
                          amountHelperText:
                            (formik.touched.buyAmount ||
                              formik.touched.buyCurrencyCode) &&
                            (formik.errors.buyAmount ||
                              formik.errors.buyCurrencyCode)
                        }}
                        equivalentProps={{
                          currencyLabel: 'Sell Currency',
                          currencyCode: formik.values.sellCurrencyCode,
                          currencyCodeOnChange: (selectedObj) => {
                            formik.setFieldValue(
                              'sellCurrencyCode',
                              selectedObj.value
                            );
                          },
                          excludedCurrencyCodes: [formik.values.buyCurrencyCode],
                          currencyCodeError:
                            formik.touched.sellCurrencyCode &&
                            !!formik.errors.sellCurrencyCode,
                          currencyCodeHelperText:
                            formik.touched.sellCurrencyCode &&
                            formik.errors.sellCurrencyCode,
                          currencyCodeSx: {
                            dropdownIndicator: {
                              paddingTop: '0',
                              paddingBottom: '0'
                            }
                          },
                          amountValue: formik.values.sellAmount,
                          amountLabel: 'Sell Amount',
                          amountValueOnChange: (newValue) => {
                            formik.setFieldValue('sellAmount', newValue);
                          },
                          onBlur: () => {
                            formik.setFieldTouched('sellAmount', true);
                          },
                          amountError:
                            (formik.touched.sellAmount ||
                              formik.touched.sellCurrencyCode) &&
                            (!!formik.errors.sellAmount ||
                              !!formik.errors.sellCurrencyCode),
                          amountHelperText:
                            (formik.touched.sellAmount ||
                              formik.touched.sellCurrencyCode) &&
                            (formik.errors.sellAmount ||
                              formik.errors.sellCurrencyCode)
                        }}
                        tradeRateProps={{
                          tradeRate: formik.values.tradeRate,
                          tradeRateOnChange: (newValue) => {
                            formik.setFieldValue('tradeRate', newValue);
                          },
                          onIsTradeRateValidChange: (newValue) => {
                            setIsTradeRateValid(newValue);
                          },
                          onBlur: () => formik.setFieldTouched('tradeRate', true),
                          error: isTriplexUnchanged
                            ? false
                            : Boolean(
                                formik.touched.tradeRate &&
                                formik.errors.tradeRate
                              ),
                          helperText: isTriplexUnchanged
                            ? undefined
                            : (formik.touched.tradeRate &&
                                formik.errors.tradeRate) || undefined,
                          disableValidation: isTriplexUnchanged,
                          style: {
                            borderRadius: '0.25rem'
                          },
                          editiconsize: '1.125rem'
                        }}
                        disableTradeRateValidation={isTriplexUnchanged}
                        profitRateProps={{
                          profitAmount: formik.values.profitAmount,
                          profitCurrency: formik.values.profitCurrency,
                          profitOnChange: (newValue) => {
                            formik.setFieldValue('profitAmount', newValue);
                          },
                          profitCodeOnChange: (newValue) => {
                            formik.setFieldValue('profitCurrency', newValue);
                          }
                        }}
                        initialCurrencyDisabled={formik.values.buyerIsBank}
                        initialAmountDisabled={
                          formik.values.buyerIsBank &&
                          !formik.values.buyerIsNewBankTransaction
                        }
                        equivalentCurrencyDisabled={formik.values.sellerIsBank}
                        equivalentAmountDisabled={
                          formik.values.sellerIsBank &&
                          !formik.values.sellerIsNewBankTransaction
                        }
                        isRateMinusEnteredRate
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <AryCurrencyTextField
                      label={`${t('Buyer Commission')} ${t('(Optional)')}`}
                      fullWidth
                      isLockable={false}
                      isCommission
                      currencyDisabled={formik.values.buyerIsBank && formik.values.buyCommissionCurrencyCode}
                      commissionComponent={
                        <CommissionComponentChip
                          value={formik.values.buyCommissionIsDebtor}
                          handleClick={() =>
                            formik.setFieldValue(
                              'buyCommissionIsDebtor',
                              !formik.values.buyCommissionIsDebtor
                            )
                          }
                          disabled={!formik.values.buyCommissionCurrencyCode}
                          trueValueLabel={t('Deposit')}
                          falseValueLabel={t('Withdraw')}
                        />
                      }
                      name="commissionAmount"
                      value={formik.values.buyCommissionAmount}
                      onChange={({ numberValue }) =>
                        formik.setFieldValue('buyCommissionAmount', numberValue)
                      }
                      currencyValue={
                        formik.values.opponentType === 2
                          ? formik.values.receivedCurrencyCode
                          : formik.values.buyCommissionCurrencyCode
                      }
                      onCurrencyChange={(selectedObj) => {
                        setHasBuyCommission(!!selectedObj?.value);
                        formik.setFieldValue(
                          'buyCommissionCurrencyCode',
                          selectedObj?.value || ''
                        );
                      }}
                      error={
                        formik.touched.buyCommissionAmount &&
                        !!formik.errors.buyCommissionAmount
                      }
                      helperText={
                        formik.touched.buyCommissionAmount &&
                        formik.errors.buyCommissionAmount
                      }
                      onBlur={() =>
                        formik.setFieldTouched('buyCommissionAmount', true)
                      }
                      dropdownIndicatorSx={{
                        paddingTop: '0',
                        paddingBottom: '0'
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AryCurrencyTextField
                      label={`${t('Seller Commission')} ${t('(Optional)')}`}
                      fullWidth
                      isLockable={false}
                      currencyDisabled={formik.values.sellerIsBank && formik.values.sellCommissionCurrencyCode}
                      isCommission
                      commissionComponent={
                        <CommissionComponentChip
                          value={formik.values.sellCommissionIsDebtor}
                          handleClick={() =>
                            formik.setFieldValue(
                              'sellCommissionIsDebtor',
                              !formik.values.sellCommissionIsDebtor
                            )
                          }
                          disabled={!formik.values.sellCommissionCurrencyCode}
                          trueValueLabel={t('Deposit')}
                          falseValueLabel={t('Withdraw')}
                        />
                      }
                      name="commissionAmount"
                      value={formik.values.sellCommissionAmount}
                      onChange={({ numberValue }) =>
                        formik.setFieldValue('sellCommissionAmount', numberValue)
                      }
                      currencyValue={
                        formik.values.opponentType === 2
                          ? formik.values.receivedCurrencyCode
                          : formik.values.sellCommissionCurrencyCode
                      }
                      onCurrencyChange={(selectedObj) => {
                        setHasSellCommission(!!selectedObj?.value);
                        formik.setFieldValue(
                          'sellCommissionCurrencyCode',
                          selectedObj?.value || ''
                        );
                      }}
                      error={
                        formik.touched.sellCommissionAmount &&
                        !!formik.errors.sellCommissionAmount
                      }
                      helperText={
                        formik.touched.sellCommissionAmount &&
                        formik.errors.sellCommissionAmount
                      }
                      onBlur={() =>
                        formik.setFieldTouched('sellCommissionAmount', true)
                      }
                      dropdownIndicatorSx={{
                        paddingTop: '0',
                        paddingBottom: '0'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={`${t('Description')} ${t('(Optional)')}`}
                      fullWidth
                      value={formik.values.description}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'description',
                          event.target.value.trimStart()
                        )
                      }
                      error={
                        formik.touched.description && !!formik.errors.description
                      }
                      helperText={
                        formik.touched.description && formik.errors.description
                      }
                      onBlur={() => {
                        formik.setFieldTouched('description', true);
                      }}
                    />
                  </Grid>
                </Grid>
              </AryWrapper>
            </Grid>
            <Grid item xs={12}>
              <AryWrapper>
                <Grid
                  container
                  rowSpacing={theme.padding.gridSmallRowSpacing}
                  columnSpacing={theme.padding.gridSmallColumnSpacing}
                >
                  <Grid item xs={12}>
                    <Typography variant="h5Bold">
                      {t('Buy Currency Setting')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    container
                    xs={12}
                    columnSpacing={theme.padding.gridSmallColumnSpacing}
                  >
                    <Grid item>
                      <SwitchableChip
                        handleClick={() =>
                          formik.setFieldValue(
                            'shouldSyncCreditLimit',
                            !formik.values.shouldSyncCreditLimit
                          )
                        }
                        value={formik.values.shouldSyncCreditLimit}
                        title={t('SyncCreditLimit')}
                        Icon={SyncCreditLimitIcon}
                      />
                    </Grid>
                    {PrivateTransactionR && (
                      <Grid item>
                        <SwitchableChip
                          title={t('private transaction')}
                          value={formik.values.isPrivate}
                          Icon={PrivateIcon}
                          handleClick={() =>
                            formik.setFieldValue(
                              'isPrivate',
                              !formik.values.isPrivate
                            )
                          }
                        />
                      </Grid>
                    )}
                    {hasWhatsappAccess && (
                      <Grid item>
                        <Grid item>
                          <WhatsappChips
                            isPublishToWhatsAppValue={
                              formik.values.isPublishToWhatsApp
                            }
                            handleChangeIsPublishToWhatsapp={(newValue) => {
                              formik.setFieldValue(
                                'isPublishToWhatsApp',
                                newValue
                              );
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid item>
                      <AryFileUploaderModal
                        files={formik.values.files}
                        fileIds={formik.values.fileIds}
                        removedFileIds={removedFileIds}
                        handleRemovedFileIds={(newFileIds) =>
                          setRemovedFileIds(newFileIds)
                        }
                        onUploadFiles={(newFiles, newFileIds) => {
                          formik.setFieldValue('files', newFiles);
                          formik.setFieldValue('fileIds', newFileIds);
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <ArySelectTransactionGroups
                      label={t('SelectGroup')}
                      isClearable
                      multiple
                      transactionCategories={formik.values.transactionCategory}
                      onTransactionCategoriesChange={(newValues) => {
                        formik.setFieldValue(
                          'transactionCategory',
                          newValues.map((newVal) => newVal.value)
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container gap={4} wrap="nowrap">
                      <Grid item xs={6}>
                        <TextField
                          label={t('Source Of Money')}
                          fullWidth
                          autoComplete="off"
                          value={formik.values.sourceOfMoney}
                          onChange={(event) =>
                            formik.setFieldValue(
                              'sourceOfMoney',
                              event.target.value.trimStart()
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label={t('Purpose Of Exchange')}
                          fullWidth
                          autoComplete="off"
                          value={formik.values.purposeOfExchange}
                          onChange={(event) =>
                            formik.setFieldValue(
                              'purposeOfExchange',
                              event.target.value.trimStart()
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AryWrapper>
            </Grid>
          </Grid>
          {!!submitButtonSize && (
            <Grid
              item
              xs={12}
              textAlign="right"
              mt={theme.margin.submitButtonTopInGrid}
            >
              <AryLoadingButton
                loading={submitTradeCurrencyLoading || updateTradeCurrencyLoading}
                size={submitButtonSize}
                variant="contained"
                type="submit"
                form="buy-currency"
                disabled={!isCalculationDone}
              >
                {t('Submit')}
              </AryLoadingButton>
            </Grid>
          )}
        </Grid>
      </form>

      <Suspense fallback={<AryLoadingAnimation />}>
        <BuyCurrencyReceiptModal
          open={modalOpen}
          handleClose={() => {
            setModalOpen(false);
            setHasBuyCommission(false);
            setHasSellCommission(false);
            handleResetForm();
          }}
          handleRepeatOperation={() => {
            setModalOpen(false);

            handleResetForm();

            const isBuyerBank = formik.values.buyerIsBank;
            const isSellerBank = formik.values.sellerIsBank;

            switch (true) {
              case !isBuyerBank && !isSellerBank:
                buyerAccountRef.current.getAccountInformation();
                sellerAccountRef.current.getAccountInformation();
                break;
              case !isBuyerBank && isSellerBank:
                buyerAccountRef.current.getAccountInformation();
                sellerBankAccountRef.current.getBankBookStatus();
                break;
              case isBuyerBank && !isSellerBank:
                buyerBankAccountRef.current.getBankBookStatus();
                sellerAccountRef.current.getAccountInformation();
                break;
              case isBuyerBank && isSellerBank:
                buyerBankAccountRef.current.getBankBookStatus();
                sellerBankAccountRef.current.getBankBookStatus();
                break;
              default:
                break;
            }
          }}
          buyerAccountNumber={formik.values.buyerAccountNumber}
          buyerIsBank={formik.values.buyerIsBank}
          sellerAccountNumber={formik.values.sellerAccountNumber}
          sellerIsBank={formik.values.sellerIsBank}
          handleChangeCustomerAccountNumber={handleChangeCustomerAccountNumber}
          handleChangeBankAccountId={handleChangeBankAccountId}
          closeParentModal={closeParentModal}
          transactionId={transactionId || submittedTransactionId}
        />
      </Suspense>
    </>
  );
};

export default BuyCurrencyBase;
